import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { getCurrentAcademicSession, getAcademicSessionSummary, isSummerVacation } from '../utils/academicSession';

function StudentAttendanceSummary({ userClass, userId, currentDate }) {
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(currentDate ? currentDate.getMonth() + 1 : new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate ? currentDate.getFullYear() : new Date().getFullYear());
  const [workingDays, setWorkingDays] = useState([]);
  const [sessionAttendance, setSessionAttendance] = useState([]);
  const [sessionWorkingDays, setSessionWorkingDays] = useState([]);
  const [loading, setLoading] = useState(false);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  useEffect(() => {
    if (currentDate) {
      setSelectedMonth(currentDate.getMonth() + 1);
      setSelectedYear(currentDate.getFullYear());
    }
  }, [currentDate]);

  useEffect(() => {
    fetchAttendanceData();
    fetchWorkingDays();
    fetchSessionData();
  }, [selectedMonth, selectedYear]);

  const fetchAttendanceData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/attendance-records/${userClass}`);
      const records = response.data || [];
      
      // Filter records for this student and selected month/year
      const studentRecords = records.filter(record => {
        const recordDate = new Date(record.date);
        return record.studentId === userId && 
               recordDate.getMonth() + 1 === selectedMonth && 
               recordDate.getFullYear() === selectedYear;
      });

      setAttendanceData(studentRecords);
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkingDays = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/working-days/${userClass}/${selectedYear}/${selectedMonth}`);
      setWorkingDays(response.data || []);
    } catch (error) {
      console.error('Error fetching working days:', error);
      setWorkingDays([]);
    }
  };

  const fetchSessionData = async () => {
    try {
      const attendanceResponse = await axios.get(`${API_BASE_URL}/api/attendance-records/${userClass}`);
      const allRecords = attendanceResponse.data || [];
      const studentRecords = allRecords.filter(record => record.studentId === userId);
      setSessionAttendance(studentRecords);

      const currentSession = getCurrentAcademicSession();
      const [startYear, endYear] = currentSession.split('-').map(Number);
      
      const sessionWorkingDaysPromises = [];
      for (let month = 4; month <= 12; month++) {
        sessionWorkingDaysPromises.push(
          axios.get(`${API_BASE_URL}/api/working-days/${userClass}/${startYear}/${month}`)
        );
      }
      for (let month = 1; month <= 3; month++) {
        sessionWorkingDaysPromises.push(
          axios.get(`${API_BASE_URL}/api/working-days/${userClass}/${endYear}/${month}`)
        );
      }

      const sessionWorkingDaysResponses = await Promise.all(sessionWorkingDaysPromises);
      const allSessionWorkingDays = sessionWorkingDaysResponses.flatMap(response => response.data || []);
      setSessionWorkingDays(allSessionWorkingDays);
    } catch (error) {
      console.error('Error fetching session data:', error);
      setSessionAttendance([]);
      setSessionWorkingDays([]);
    }
  };

  const getAttendanceStats = () => {
    const presentDays = attendanceData.filter(record => record.status === 'present').length;
    const absentDays = attendanceData.filter(record => record.status === 'absent').length;
    const totalMarked = presentDays + absentDays;
    const totalWorkingDays = workingDays.length;
    const unmarkedDays = totalWorkingDays - totalMarked;
    const percentage = totalMarked > 0 ? ((presentDays / totalMarked) * 100).toFixed(1) : 0;
    const overallPercentage = totalWorkingDays > 0 ? ((presentDays / totalWorkingDays) * 100).toFixed(1) : 0;

    return {
      presentDays,
      absentDays,
      totalMarked,
      totalWorkingDays,
      unmarkedDays,
      percentage,
      overallPercentage
    };
  };

  const getAttendanceByWeek = () => {
    const weeks = {};
    const year = selectedYear;
    const month = selectedMonth - 1; // JavaScript months are 0-indexed
    
    // Get first day of month and calculate weeks
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    for (let date = new Date(firstDay); date <= lastDay; date.setDate(date.getDate() + 1)) {
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!weeks[weekKey]) {
        weeks[weekKey] = {
          weekStart: new Date(weekStart),
          days: [],
          present: 0,
          absent: 0,
          unmarked: 0
        };
      }
      
      const dateStr = date.toISOString().split('T')[0];
      const isWorkingDay = workingDays.includes(dateStr);
      const attendanceRecord = attendanceData.find(record => record.date === dateStr);
      
      if (isWorkingDay) {
        weeks[weekKey].days.push({
          date: new Date(date),
          dateStr,
          status: attendanceRecord?.status || 'unmarked',
          isWorkingDay: true
        });
        
        if (attendanceRecord?.status === 'present') {
          weeks[weekKey].present++;
        } else if (attendanceRecord?.status === 'absent') {
          weeks[weekKey].absent++;
        } else {
          weeks[weekKey].unmarked++;
        }
      }
    }
    
    return Object.values(weeks).filter(week => week.days.length > 0);
  };

  const stats = getAttendanceStats();
  const weeklyData = getAttendanceByWeek();
  const sessionSummary = getAcademicSessionSummary(sessionAttendance, sessionWorkingDays);

  return (
    <div style={{ 
      background: 'white', 
      borderRadius: '12px', 
      padding: '20px', 
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      border: '1px solid #e3f2fd'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h3 style={{ margin: 0, color: '#1565c0', fontSize: '20px', fontWeight: 'bold' }}>
            📊 My Attendance Summary
          </h3>
          <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
            Academic Session: {getCurrentAcademicSession()} (Started in April)
            {currentDate && ' | Synchronized with calendar above'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {currentDate ? (
            <div style={{ 
              padding: '6px 12px', 
              borderRadius: '5px', 
              border: '1px solid #ddd', 
              fontSize: '14px',
              background: '#f8f9fa',
              color: '#495057',
              fontWeight: '600'
            }}>
              {monthNames[selectedMonth - 1]} {selectedYear}
            </div>
          ) : (
            <>
              <select 
                value={selectedMonth} 
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                style={{ padding: '6px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '14px' }}
              >
                {monthNames.map((month, index) => (
                  <option key={index} value={index + 1}>{month}</option>
                ))}
              </select>
              <select 
                value={selectedYear} 
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{ padding: '6px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '14px' }}
              >
                {[2023, 2024, 2025, 2026].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>
          <div style={{ fontSize: '16px' }}>Loading attendance data...</div>
        </div>
      ) : (
        <>
          {/* Academic Session Summary */}
          <div style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            color: 'white', 
            padding: '20px', 
            borderRadius: '12px', 
            marginBottom: '20px',
            boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
          }}>
            <h4 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold' }}>
              🎓 Academic Session {sessionSummary.academicSession} Overview
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '15px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '5px' }}>
                  {sessionSummary.totalPresent}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>Total Present</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '5px' }}>
                  {sessionSummary.totalAbsent}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>Total Absent</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '5px' }}>
                  {sessionSummary.totalSummerVacationDays}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>Summer Vacation</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '5px' }}>
                  {sessionSummary.overallPercentage}%
                </div>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>Session %</div>
              </div>
            </div>
            <div style={{ 
              marginTop: '15px', 
              padding: '10px', 
              background: 'rgba(255,255,255,0.2)', 
              borderRadius: '8px',
              fontSize: '12px',
              textAlign: 'center'
            }}>
              📅 Session: April {sessionSummary.academicSession.split('-')[0]} - March {sessionSummary.academicSession.split('-')[1]} | 
              🏖️ Summer Break: May 16 - June 30
            </div>
          </div>

          {/* Monthly Summary Cards */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
            gap: '15px', 
            marginBottom: '25px' 
          }}>
            <div style={{ 
              background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)', 
              color: 'white', 
              padding: '15px', 
              borderRadius: '10px', 
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '5px' }}>
                {stats.presentDays}
              </div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>Present Days</div>
            </div>
            
            <div style={{ 
              background: 'linear-gradient(135deg, #f44336 0%, #e53935 100%)', 
              color: 'white', 
              padding: '15px', 
              borderRadius: '10px', 
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '5px' }}>
                {stats.absentDays}
              </div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>Absent Days</div>
            </div>
            
            <div style={{ 
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)', 
              color: 'white', 
              padding: '15px', 
              borderRadius: '10px', 
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '5px' }}>
                {stats.unmarkedDays}
              </div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>Unmarked Days</div>
            </div>
            
            <div style={{ 
              background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)', 
              color: 'white', 
              padding: '15px', 
              borderRadius: '10px', 
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '5px' }}>
                {stats.overallPercentage}%
              </div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>Overall %</div>
            </div>
          </div>

          {/* Detailed Stats */}
          <div style={{ 
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', 
            padding: '15px', 
            borderRadius: '10px', 
            marginBottom: '20px',
            border: '1px solid #dee2e6'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#495057', fontSize: '16px' }}>
              📈 Detailed Statistics
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              <div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                  <strong>Attendance Rate (Marked Days):</strong>
                </div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: stats.percentage >= 75 ? '#4caf50' : stats.percentage >= 50 ? '#ff9800' : '#f44336' }}>
                  {stats.percentage}% ({stats.presentDays}/{stats.totalMarked})
                </div>
              </div>
              <div>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                  <strong>Overall Rate (All Working Days):</strong>
                </div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: stats.overallPercentage >= 75 ? '#4caf50' : stats.overallPercentage >= 50 ? '#ff9800' : '#f44336' }}>
                  {stats.overallPercentage}% ({stats.presentDays}/{stats.totalWorkingDays})
                </div>
              </div>
            </div>
          </div>



          {stats.totalWorkingDays === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '30px', 
              color: '#666',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>📅</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '5px' }}>
                No Working Days Found
              </div>
              <div style={{ fontSize: '14px' }}>
                No working days are scheduled for {monthNames[selectedMonth - 1]} {selectedYear}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default StudentAttendanceSummary;